import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  Download,
  ShieldCheck,
  ArrowRight,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import {
  PH_KEY_FACTS,
  PH_SERVICES,
  PH_SAMPLE_MENU_TABLE,
  PH_FESTIVALS,
  PH_KITCHEN,
  PH_PILLARS,
  PH_CERTS,
  PH_CLIENTS_CATERING,
  PH_CLIENTS_EVENTS,
  PH_CLIENT_LOGOS,
  PH_EVENT_FORMATS,
} from "@/data/content";
import { PHWordmark } from "@/components/site/Wordmarks";
import Seo from "@/components/site/Seo";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

const CERT_HIGHLIGHT = new Set([
  "FSSAI Licensed",
  "FSSAI Food Handlers (Individual)",
  "HACCP Certified",
  "NABL Food & Water Testing",
  "Fire NOC",
  "GST Registered",
  "MSME Registered",
  "PF / ESI Compliant",
]);

const PROFILE_PDF_URL =
  "/assets/platinum-hospitality-company-profile-2026.pdf";
const PROFILE_PDF_FILENAME =
  "Platinum-Hospitality-Company-Profile-2026.pdf";

function handleProfileDownload() {
  toast.success("Downloading Platinum Hospitality Company Profile…");
}

export default function PlatinumHospitality() {
  const [menuIdx, setMenuIdx] = useState(0);

  return (
    <div className="bg-[#FDFBF6]">
      <Seo
        title="Corporate Catering Services Gurugram — Platinum Hospitality | 25 Years, 20+ MNC Clients"
        description="Platinum Hospitality — corporate catering services in Gurugram, Delhi NCR. 25+ years, 20+ MNC clients, 4,500+ meals daily. FSSAI-certified corporate cafeteria services from Udyog Vihar."
        path="/platinum-hospitality"
        keywords="corporate catering Gurugram, corporate cafeteria services Gurgaon, catering company Udyog Vihar, FSSAI certified caterer Gurgaon, MNC catering Delhi NCR, industrial catering Gurgaon, corporate meals Gurugram"
      />
      {/* HERO — navy */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-24 bg-[#2B3A8C] text-[#FDFBF6] overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-15" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
          <div className="inline-block bg-[#FDFBF6] px-4 py-3 rounded">
            <PHWordmark height={48} />
          </div>
          <div className="mt-6 text-[11px] uppercase tracking-[0.3em] text-[#C9A961]">
            Catering Solutions for Corporates · Since 2001
          </div>
          <h1 className="mt-6 font-serif font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.02] max-w-5xl">
            Transforming everyday dining into <em className="italic text-[#C9A961]">extraordinary</em> experiences.
          </h1>
          <p className="mt-8 max-w-2xl text-[#FDFBF6]/85 leading-relaxed">
            Platinum Hospitality is a leading corporate catering company in
            Gurugram with a rich legacy of over 25 years. We proudly serve all
            three main meals — Breakfast, Lunch &amp; Dinner — plus hi-tea
            snacks every day to over 2,500 individuals across 20+ MNC
            corporates in Delhi NCR. Our FSSAI-certified, newly renovated
            6,000 sq ft central kitchen in <strong>Udyog Vihar, Gurgaon</strong>
            {" "}positions us at the heart of the corporate hub — delivering
            reliable corporate cafeteria services across Gurugram, Noida &amp;
            Faridabad.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/book?brand=ph"
              className="inline-flex items-center gap-3 h-12 px-6 rounded-full bg-[#D42B2B] text-[#FDFBF6] text-xs uppercase tracking-[0.28em] hover:bg-[#C9A961] hover:text-[#3B1F1A] transition-colors"
            >
              Partner With Us
            </Link>
            <a
              href={PROFILE_PDF_URL}
              download={PROFILE_PDF_FILENAME}
              onClick={handleProfileDownload}
              data-testid="ph-download-profile-hero"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-full border border-[#FDFBF6]/30 text-[#FDFBF6] text-xs uppercase tracking-[0.28em] hover:border-[#C9A961] hover:text-[#C9A961]"
            >
              <Download size={14} /> Company Profile
            </a>
          </div>
        </div>
      </section>

      {/* KEY FACTS DASHBOARD — big red numbers, navy labels */}
      <section className="relative py-20 md:py-28 border-b border-[#C9A961]/25">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-14">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#D42B2B]">By the numbers</div>
            <h2 className="mt-6 font-serif font-light text-4xl md:text-5xl text-[#2B3A8C]">
              Twenty-five years, in one glance.
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {PH_KEY_FACTS.map((f, i) => (
              <motion.div
                key={f.v}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ delay: i * 0.06, duration: 0.6 }}
                className="relative p-8 bg-[#FDFBF6] border border-[#2B3A8C]/10 hover:border-[#D42B2B]/40 transition-colors group"
              >
                <span className="absolute left-0 top-8 bottom-8 w-1 bg-[#D42B2B]" />
                <div className="font-serif text-6xl md:text-7xl leading-none text-[#D42B2B]">
                  {f.k}
                </div>
                <div className="mt-4 text-[11px] uppercase tracking-[0.24em] text-[#2B3A8C]">
                  {f.v}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="relative bg-[#F5C9B0]/20 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-16">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#D42B2B]">
              Corporate Catering Services
            </div>
            <h2 className="mt-6 font-serif font-light text-4xl md:text-5xl text-[#2B3A8C] max-w-3xl">
              What we cook, every day, at scale.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PH_SERVICES.map((s, i) => (
              <motion.div
                key={s.title}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                transition={{ delay: i * 0.05 }}
                className="bg-[#FDFBF6] p-8 border border-[#2B3A8C]/10 hover:border-[#D42B2B]/40 transition-colors"
              >
                <div className="font-serif text-2xl text-[#2B3A8C]">{s.title}</div>
                <p className="mt-4 text-sm text-[#3B1F1A]/75 leading-relaxed">{s.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* KITCHEN OPS — process flow */}
      <section className="bg-[#2B3A8C] text-[#FDFBF6] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-14">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961]">Kitchen Operations</div>
            <h2 className="mt-6 font-serif font-light text-4xl md:text-5xl">
              6,000 sq ft. Six zones. <em className="italic text-[#C9A961]">Zero cross-contamination.</em>
            </h2>
            <p className="mt-6 max-w-2xl text-[#FDFBF6]/80 leading-relaxed">
              Every ingredient moves through a strict one-way flow. Vegetarian and non-vegetarian workstreams are physically separated at the cutting and cooking stages — a hard rule, honoured for every corporate menu we run.
            </p>
          </div>

          {/* Horizontal process flow (desktop) */}
          <div className="hidden md:block relative overflow-x-auto pb-4">
            <div className="absolute top-[70px] left-8 right-8 h-px bg-[#C9A961]/50" aria-hidden />
            <ol className="relative flex items-start justify-between gap-4 min-w-max">
              {PH_KITCHEN.map((k, i) => {
                const separator = k.title.startsWith("Cutting") || k.title.startsWith("Cooking");
                return (
                  <li key={k.title} className="flex-1 min-w-[180px] flex flex-col items-center text-center px-2">
                    <div className={`relative z-10 h-14 w-14 rounded-full border ${separator ? "border-[#C9A961] bg-[#D42B2B]" : "border-[#C9A961] bg-[#2B3A8C]"} flex items-center justify-center font-mono text-sm text-[#FDFBF6] shadow-[0_0_0_6px_rgba(43,58,140,1)]`}>
                      0{i + 1}
                    </div>
                    <div className="mt-6 font-serif text-lg text-[#FDFBF6]">{k.title}</div>
                    <p className="mt-2 text-xs text-[#FDFBF6]/70 leading-relaxed max-w-[200px]">{k.body}</p>
                    {separator && (
                      <span className="mt-3 inline-flex items-center h-6 px-2 rounded-sm bg-[#D42B2B]/25 text-[10px] uppercase tracking-widest text-[#C9A961]">
                        Veg / Non-Veg Split
                      </span>
                    )}
                  </li>
                );
              })}
            </ol>
          </div>

          {/* Vertical process flow (mobile) */}
          <ol className="md:hidden space-y-4">
            {PH_KITCHEN.map((k, i) => {
              const separator = k.title.startsWith("Cutting") || k.title.startsWith("Cooking");
              return (
                <li key={k.title} className="flex items-start gap-4 p-5 border border-[#FDFBF6]/15">
                  <div className={`h-10 w-10 rounded-full border border-[#C9A961] ${separator ? "bg-[#D42B2B]" : "bg-transparent"} flex items-center justify-center font-mono text-xs`}>
                    0{i + 1}
                  </div>
                  <div>
                    <div className="font-serif text-lg">{k.title}</div>
                    <p className="mt-1 text-xs text-[#FDFBF6]/75 leading-relaxed">{k.body}</p>
                    {separator && (
                      <span className="mt-2 inline-flex items-center h-5 px-2 rounded-sm bg-[#D42B2B]/25 text-[9px] uppercase tracking-widest text-[#C9A961]">
                        Veg / Non-Veg Split
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>

          <p className="mt-10 text-sm text-[#FDFBF6]/70 max-w-3xl leading-relaxed border-t border-[#C9A961]/25 pt-6">
            HACCP-guided, ISO-style documentation. Every batch is traceable to its supplier and prep-team on a given date. This is the same paperwork we&rsquo;re able to hand to your food-safety auditor.
          </p>
        </div>
      </section>

      {/* CLIENT LOGOS MARQUEE */}
      <ClientMarquee />

      {/* COMPLIANCE / CERTS — badges */}
      <section id="profile" className="py-24 md:py-32 bg-[#FDFBF6]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-14">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#D42B2B]">
              Compliance & Certifications
            </div>
            <h2 className="mt-6 font-serif font-light text-4xl md:text-5xl text-[#2B3A8C]">
              Enterprise-grade, on paper and in the kitchen.
            </h2>
            <p className="mt-6 max-w-2xl text-[#3B1F1A]/75 leading-relaxed">
              We hold the org-level FSSAI license — <span className="text-[#D42B2B] font-medium">and every one of our kitchen staff carries an individual FSSAI Food Handlers License</span>. Not a policy on paper. A file on every hire.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-6">
            {PH_CERTS.map((c, i) => {
              const highlight = CERT_HIGHLIGHT.has(c);
              const isIndividual = c.includes("Individual");
              return (
                <motion.div
                  key={c}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ delay: i * 0.04, duration: 0.5 }}
                  className={`relative p-6 bg-[#FDFBF6] border ${isIndividual ? "border-[#D42B2B]/60 bg-[#D42B2B]/5" : "border-[#C9A961]/40"} flex flex-col items-center text-center`}
                >
                  <div className={`h-14 w-14 rounded-full flex items-center justify-center ${isIndividual ? "bg-[#D42B2B] text-[#FDFBF6]" : "bg-[#2B3A8C] text-[#C9A961]"}`}>
                    <ShieldCheck size={22} strokeWidth={1.6} />
                  </div>
                  <div className={`mt-4 text-xs md:text-sm font-medium leading-tight ${isIndividual ? "text-[#D42B2B]" : "text-[#2B3A8C]"}`}>
                    {c}
                  </div>
                  {highlight && (
                    <div className="mt-2 text-[9px] uppercase tracking-widest text-[#C9A961]">
                      Verified
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-24 md:py-32 bg-[#F5C9B0]/20">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-16">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#D42B2B]">Why choose Platinum Hospitality</div>
            <h2 className="mt-6 font-serif font-light text-4xl md:text-5xl text-[#2B3A8C]">
              Seven reasons, twenty-five years old.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {PH_PILLARS.map((p, i) => (
              <motion.article
                key={p.n}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ delay: i * 0.04 }}
                className="p-8 bg-[#FDFBF6] border border-[#2B3A8C]/10 hover:border-[#D42B2B]/50 transition-colors"
              >
                <div className="flex items-baseline justify-between">
                  <span className="font-serif text-5xl text-[#C9A961]">{p.n}</span>
                  <span className="h-px w-10 bg-[#D42B2B]" />
                </div>
                <h3 className="mt-4 font-serif text-2xl text-[#2B3A8C]">{p.title}</h3>
                <p className="mt-3 text-sm text-[#3B1F1A]/75 leading-relaxed">{p.body}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* SAMPLE MENU — tabbed table */}
      <section className="py-24 md:py-32 bg-[#FDFBF6]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#D42B2B]">
                Weekly Sample Menu
              </div>
              <h2 className="mt-6 font-serif font-light text-4xl md:text-5xl text-[#2B3A8C]">
                What lunch looks like on any given day.
              </h2>
            </div>
            <p className="text-sm text-[#3B1F1A]/70 max-w-sm">
              One indicative week from our corporate cafeteria rotation — dietician-checked, refreshed quarterly against your brief.
            </p>
          </div>

          {/* Day tabs */}
          <div className="flex flex-wrap gap-2 mb-6">
            {PH_SAMPLE_MENU_TABLE.map((row, i) => (
              <button
                key={row.day}
                onClick={() => setMenuIdx(i)}
                className={`text-[11px] uppercase tracking-[0.24em] px-4 h-9 rounded-full border transition-colors ${
                  menuIdx === i
                    ? "bg-[#2B3A8C] text-[#FDFBF6] border-[#2B3A8C]"
                    : "border-[#2B3A8C]/20 text-[#2B3A8C] hover:border-[#D42B2B] hover:text-[#D42B2B]"
                }`}
              >
                {row.day}
              </button>
            ))}
          </div>

          {/* Table */}
          <div className="overflow-x-auto border border-[#C9A961]/40">
            <table className="w-full min-w-[720px] border-collapse">
              <thead>
                <tr className="bg-[#2B3A8C] text-[#FDFBF6]">
                  {["Day", "Dal", "Vegetable", "Rice", "Curd / Raita", "Sweet"].map((h) => (
                    <th key={h} className="text-left px-5 py-4 text-[10px] uppercase tracking-[0.24em] font-medium">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {PH_SAMPLE_MENU_TABLE.map((row, i) => (
                  <motion.tr
                    key={row.day}
                    animate={{
                      backgroundColor: menuIdx === i ? "rgba(212, 43, 43, 0.06)" : "rgba(253, 251, 246, 1)",
                    }}
                    className="border-t border-[#C9A961]/25"
                  >
                    <td className={`px-5 py-4 font-serif text-lg ${menuIdx === i ? "text-[#D42B2B]" : "text-[#2B3A8C]"}`}>
                      {row.day}
                    </td>
                    <td className="px-5 py-4 text-sm text-[#3B1F1A]">{row.dal}</td>
                    <td className="px-5 py-4 text-sm text-[#3B1F1A]">{row.veg}</td>
                    <td className="px-5 py-4 text-sm text-[#3B1F1A]">{row.rice}</td>
                    <td className="px-5 py-4 text-sm text-[#3B1F1A]">{row.curd}</td>
                    <td className="px-5 py-4 text-sm text-[#3B1F1A]">{row.sweet}</td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Food festival strip */}
          <div className="mt-16">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#D42B2B]">
              Food Festival Rotation
            </div>
            <h3 className="mt-4 font-serif text-3xl text-[#2B3A8C]">Weekly regional pop-ups in the cafeteria.</h3>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-5 gap-3">
              {PH_FESTIVALS.map((f, i) => (
                <motion.div
                  key={f.day}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="peach-gradient border border-[#C9A961]/40 p-5"
                >
                  <div className="text-[10px] uppercase tracking-[0.24em] text-[#D42B2B]">{f.day}</div>
                  <div className="mt-1 font-serif text-xl text-[#2B3A8C]">{f.region}</div>
                  <ul className="mt-3 text-xs text-[#3B1F1A]/80 space-y-1">
                    {f.items.map((it) => (
                      <li key={it}>· {it}</li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* EVENTS SECTION */}
      <section className="bg-[#F5C9B0]/25 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#D42B2B]">
              Events & Experiential
            </div>
            <h2 className="mt-6 font-serif font-light text-4xl md:text-5xl text-[#2B3A8C]">
              A seamless extension of everyday excellence.
            </h2>
            <p className="mt-6 text-[#3B1F1A]/75 leading-relaxed max-w-md">
              The same team that runs your daily cafeteria plates your annual day,
              your product launch, and your leadership retreat.
            </p>
          </div>
          <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {PH_EVENT_FORMATS.map((e) => (
              <div key={e} className="p-5 bg-[#FDFBF6] border border-[#2B3A8C]/10 flex items-start gap-3">
                <CheckCircle2 size={16} className="text-[#D42B2B] mt-1 flex-shrink-0" />
                <span className="text-sm text-[#3B1F1A]">{e}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CUSTOMER SUPPORT */}
      <section className="py-24 md:py-28 bg-[#FDFBF6]">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#D42B2B]">
            Dedicated customer support
          </div>
          <h2 className="mt-6 font-serif font-light text-3xl md:text-4xl text-[#2B3A8C] leading-[1.15]">
            From initial consultations to post-event feedback, our team is <em className="italic text-[#C9A961]">always</em> available.
          </h2>
          <p className="mt-6 text-sm text-[#3B1F1A]/70 max-w-2xl mx-auto leading-relaxed">
            One point of contact from proposal to plate — attentive, responsive, and briefed on your account&rsquo;s history from day one.
          </p>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative bg-[#2B3A8C] text-[#FDFBF6] py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-15" />
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center relative">
          <h2 className="font-serif font-light text-4xl md:text-6xl leading-[1.05]">
            Ready to talk about your <em className="italic text-[#C9A961]">cafeteria</em>?
          </h2>
          <p className="mt-6 max-w-xl mx-auto text-[#FDFBF6]/75 leading-relaxed">
            Send us your headcount, dietary matrix and site, and we&rsquo;ll come back with a proposal within one working day.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link
              to="/book?brand=ph"
              className="inline-flex items-center gap-3 h-12 px-6 rounded-full bg-[#D42B2B] text-[#FDFBF6] text-xs uppercase tracking-[0.28em] hover:bg-[#C9A961] hover:text-[#3B1F1A] transition-colors"
            >
              Partner With Us <ArrowRight size={14} />
            </Link>
            <a
              href={PROFILE_PDF_URL}
              download={PROFILE_PDF_FILENAME}
              onClick={handleProfileDownload}
              data-testid="ph-download-profile-cta"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-full border border-[#FDFBF6]/30 text-[#FDFBF6] text-xs uppercase tracking-[0.28em] hover:border-[#C9A961] hover:text-[#C9A961]"
            >
              <Download size={14} /> Download Company Profile
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

function ClientMarquee() {
  const clients = [...PH_CLIENTS_CATERING, ...PH_CLIENTS_EVENTS];
  // duplicate for seamless loop
  const loop = [...clients, ...clients];
  return (
    <section className="relative py-20 md:py-24 bg-[#1A100D] text-[#FDFBF6] overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10 mb-10">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961]">Trusted by</div>
        <h2 className="mt-4 font-serif font-light text-3xl md:text-4xl">
          Twenty-plus MNCs across Delhi NCR &amp; beyond.
        </h2>
      </div>

      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#1A100D] to-transparent z-10" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-[#1A100D] to-transparent z-10" />
        <motion.div
          className="flex w-max gap-4 pr-4 whitespace-nowrap will-change-transform"
          animate={{ x: ["0%", "-50%"] }}
          transition={{ duration: 60, ease: "linear", repeat: Infinity }}
        >
          {loop.map((c, i) => {
            const [name, city] = c.split(" · ");
            const logo = PH_CLIENT_LOGOS[name];
            return (
              <div
                key={`${c}-${i}`}
                data-testid={`client-marquee-tile-${i}`}
                className="shrink-0 w-[300px] p-5 border border-[#FDFBF6]/15 hover:border-[#C9A961]/60 transition-colors flex items-center gap-4"
              >
                {logo ? (
                  <div className="h-12 w-20 rounded bg-white flex items-center justify-center p-1.5 shrink-0">
                    <img
                      src={logo}
                      alt={`${name} logo — Platinum Hospitality corporate catering client`}
                      loading="eager"
                      decoding="async"
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="h-12 w-12 rounded-full bg-[#2B3A8C] flex items-center justify-center font-serif text-lg text-[#C9A961] shrink-0">
                    {name.split(" ").slice(0, 2).map((w) => w[0]).join("")}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="text-sm text-[#FDFBF6] truncate">{name}</div>
                  <div className="text-[10px] uppercase tracking-[0.24em] text-[#C9A961] truncate">
                    {city}
                  </div>
                </div>
                <ChevronRight size={14} className="text-[#FDFBF6]/30 ml-auto flex-shrink-0" />
              </div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
